import {
  CalendarClock,
  CheckCircle,
  Loader2,
  LucideIcon,
  XCircle,
} from 'lucide-react';
import _ from 'lodash';
import { format as tzFormat } from 'date-fns-tz';

import {
  EdgeStack,
  EdgeStackStatus,
  StatusType,
} from '@/react/edge/edge-stacks/types';
import { semverCompare } from '@/react/common/semver-utils';

import { IconMode } from '@@/Icon';

export function compareVersion(
  currentVersion: string,
  version = '',
  bigger = false
) {
  if (!currentVersion) {
    return true;
  }

  // if supplied version is not a string, e.g develop
  if (!version.includes('.')) {
    return true;
  }

  if (bigger) {
    return semverCompare(currentVersion, version) > 0;
  }

  // env version is less than the supplied
  return semverCompare(currentVersion, version) < 0;
}

export function getEnvironmentStatus(status: EdgeStackStatus): {
  icon?: LucideIcon;
  spin?: boolean;
  mode?: IconMode;
  tooltip?: string;
} {
  const lastStatus = _.last(status.Status);

  const isFailed = lastStatus?.Type === StatusType.Error;
  if (isFailed) {
    return {
      icon: XCircle,
      mode: 'danger',
      tooltip: 'Update failed',
    };
  }

  const isCompleted =
    lastStatus?.Type === StatusType.Completed ||
    lastStatus?.Type === StatusType.RemoteUpdateSuccess;

  if (isCompleted) {
    return {
      icon: CheckCircle,
      mode: 'success',
      tooltip: 'Update completed',
    };
  }

  const isPending = lastStatus?.Type === StatusType.Pending;
  if (isPending) {
    return {
      icon: CalendarClock,
      mode: 'primary',
      tooltip: 'Update pending',
    };
  }

  return {
    icon: Loader2,
    spin: true,
    mode: 'primary',
    tooltip: 'Updating',
  };
}

export function aggregateStackStatus(stackStatus: EdgeStack['Status']) {
  const aggregateStatus: Partial<Record<StatusType, number>> = {};

  return Object.values(stackStatus).reduce(
    (acc, envStatus) =>
      envStatus.Status.reduce((acc, status) => {
        const { Type } = status;
        acc[Type] = (acc[Type] || 0) + 1;
        return acc;
      }, acc),
    aggregateStatus
  );
}

export function formatDate(
  date: Date | string | number,
  timeZone: string | null = null
): string {
  const format = 'yyyy-MM-dd | HH:mm:ss';

  if (!timeZone) {
    try {
      return tzFormat(date, format);
    } catch {
      return '';
    }
  }

  try {
    return tzFormat(date, format, { timeZone });
  } catch {
    return '';
  }
}

export function parseEnvironmentTimezone(
  localTimeZone: string | undefined
): string {
  if (!localTimeZone) {
    return 'UTC';
  }

  try {
    // Check if the localTimeZone is a valid IANA timezone
    Intl.DateTimeFormat(undefined, { timeZone: localTimeZone });
  } catch {
    // If not valid, default to UTC
    return 'UTC';
  }

  return localTimeZone;
}
